// "Duplicate funding asks" card — design §6.3's left-rail "Same funding
// programme mentioned by multiple projects" table. Story #5305 Task #5313
// (Stream 13g) built the backend for this (whitespace/overlap_funding.py's
// build_funding_rollup) but never wired it to a live route or a UI — Task
// #5321's P1 review closed that gap (ADO Bug #5338 has the full trace) by
// adding the GET .../portfolio_overlap/funding route and this card.
//
// Deterministic, no AI: every row is a regex-extracted funding-programme
// mention across >=2 of the viewer's own entitled projects (backend's own
// module docstring). A row with no `group_id` means the mentioning
// projects never formed a scored overlap group — design's own "no overlap
// group — review" state — surfaced here as a plain notice rather than a
// broken link.
import { useQuery } from '@tanstack/react-query'
import { Coins } from 'lucide-react'
import { getPortfolioOverlapFunding } from '../../services/api'
import { useAggregator } from '../../contexts/AggregatorContext'

interface OverlapFundingCardProps {
  /** Switches OverlapView to a linked row's group, mirroring the Synergy
   * Matrix's own cross-link callback shape. Omitted rows without a
   * group_id render their plain "no overlap group" notice instead. */
  onOpenGroup: (groupId: string) => void
}

function formatAmountTotals(totals: Record<string, number>): string | null {
  const entries = Object.entries(totals).filter(([, amount]) => amount > 0)
  if (entries.length === 0) return null
  return entries
    .map(([currency, amount]) => `${currency} ${amount.toLocaleString()}`)
    .join(' · ')
}

export default function OverlapFundingCard({ onOpenGroup }: OverlapFundingCardProps) {
  const { activeAggregatorId } = useAggregator()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['portfolio-overlap-funding', activeAggregatorId],
    queryFn: () => getPortfolioOverlapFunding(activeAggregatorId),
  })

  if (isLoading) {
    return (
      <div className="card p-2.5" data-testid="overlap-funding-loading">
        <p className="text-[11px] text-gray-400">Loading funding mentions…</p>
      </div>
    )
  }

  // A read failure here must never block the rest of the Overlap tab —
  // the groups list above already rendered successfully. Fail quiet, not
  // loud, on a card that is informational rather than load-bearing.
  if (isError || !data) {
    return null
  }

  const rows = data.funding_mentions_rows

  if (rows.length === 0) {
    return (
      <div className="card p-2.5" data-testid="overlap-funding-empty">
        <p className="text-[11px] text-gray-500 leading-relaxed">
          No funding programme is currently mentioned by two or more of your entitled projects.
        </p>
      </div>
    )
  }

  const amountLine = formatAmountTotals(data.total_amount_by_currency)

  return (
    <div className="card p-2.5" data-testid="overlap-funding-card">
      <div className="flex items-center gap-1.5">
        <Coins className="w-3.5 h-3.5 text-amber-600" />
        <b className="text-xs text-iflow-900">Duplicate funding asks</b>
      </div>
      {amountLine && (
        <p className="text-[10.5px] text-gray-500 mt-0.5">
          {data.total_documents_with_funding_amounts} document{data.total_documents_with_funding_amounts === 1 ? '' : 's'} mention an amount · {amountLine}
        </p>
      )}
      <ul className="mt-1.5 space-y-1.5">
        {rows.map((row) => {
          const label = [row.programme, row.identifier].filter(Boolean).join(' ') || 'Unnamed programme'
          const projectNames = row.mentions.map((m) => m.project_name)
          return (
            <li key={`${row.programme ?? ''}-${row.identifier ?? ''}`} className="text-[10.5px] leading-relaxed" data-testid="overlap-funding-row">
              <b className="text-iflow-900">{label}</b> — named in {projectNames.join(' and ')}
              {row.group_id ? (
                <>
                  {' → '}
                  <button
                    type="button"
                    onClick={() => onOpenGroup(row.group_id as string)}
                    className="text-iflow-700 hover:text-iflow-800 font-semibold hover:underline"
                    data-testid="overlap-funding-row-open-group"
                  >
                    view overlap group
                  </button>
                </>
              ) : (
                <span className="text-amber-700"> → no overlap group — review</span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
